import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { useToast } from '../hooks/use-toast';
import PageHeader from './ui/PageHeader';
import {
  Trophy, Award, Star, Target, Zap, TrendingUp, Medal,
  Crown, Gift, Flame, Calendar, Users, Clock, CheckCircle,
  Lock, Unlock, ChevronRight, Sparkles, Shield, BookOpen,
  AlertTriangle, Lightbulb, Heart, RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Tier colors
const TIER_COLORS = {
  bronze: 'from-amber-600 to-amber-700',
  silver: 'from-gray-400 to-gray-500',
  gold: 'from-yellow-400 to-yellow-500',
  platinum: 'from-purple-400 to-purple-500'
};

const TIER_BG = {
  bronze: 'bg-amber-100 border-amber-200 text-amber-800',
  silver: 'bg-gray-100 border-gray-200 text-gray-800',
  gold: 'bg-yellow-100 border-yellow-200 text-yellow-800',
  platinum: 'bg-purple-100 border-purple-200 text-purple-800'
};

const CATEGORY_ICONS = {
  training: BookOpen,
  safety: Shield,
  compliance: CheckCircle,
  leadership: Crown,
  innovation: Lightbulb,
  teamwork: Users
};

const GamificationDashboard = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboard, setDashboard] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [allBadges, setAllBadges] = useState({ earned: [], inProgress: [] });
  const [challenges, setChallenges] = useState([]);
  const [userId, setUserId] = useState(null);

  // Get user ID from localStorage
  useEffect(() => {
    const userData = localStorage.getItem('user_data');
    if (userData) {
      const user = JSON.parse(userData);
      setUserId(user.id);
    }
  }, []);

  // Scroll to top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Load data when userId is available
  useEffect(() => {
    if (userId) {
      loadDashboard();
    }
  }, [userId]);

  const loadDashboard = async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const [dashboardRes, leaderboardRes, badgesRes, challengesRes] = await Promise.all([
        axios.get(`${API}/gamification/dashboard/${userId}`).catch(() => ({ data: null })),
        axios.get(`${API}/gamification/leaderboard?current_user_id=${userId}`).catch(() => ({ data: { leaderboard: [] } })),
        axios.get(`${API}/gamification/badges/${userId}`).catch(() => ({ data: { earned_badges: [], in_progress_badges: [] } })),
        axios.get(`${API}/gamification/challenges`).catch(() => ({ data: { challenges: [] } }))
      ]);
      
      setDashboard(dashboardRes.data);
      setLeaderboard(leaderboardRes.data?.leaderboard || []);
      setAllBadges({
        earned: badgesRes.data?.earned_badges || [],
        inProgress: badgesRes.data?.in_progress_badges || []
      });
      setChallenges(challengesRes.data?.challenges || []);
    } catch (error) {
      console.error('Error loading gamification data:', error);
      // Set defaults for demo
      setDashboard({
        points: { total_points: 450, level: 3, current_level_points: 150, points_to_next_level: 250, streak_days: 5 },
        badges: { earned: [], in_progress: [], total_earned: 3, total_available: 20 },
        stats: { trainings_completed: 5, observations_reported: 12, audits_completed: 2 },
        challenges: [],
        recent_activity: [],
        leaderboard_position: { rank: 7, total_points: 450 },
        top_performers: []
      });
    } finally {
      setLoading(false);
    }
  };

  const joinChallenge = async (challengeId) => {
    if (!userId) return;
    
    try {
      await axios.post(`${API}/gamification/challenges/${challengeId}/join?user_id=${userId}`);
      toast({
        title: "Challenge Joined!",
        description: "You've joined the challenge. Good luck!",
      });
      loadDashboard();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to join challenge",
        variant: "destructive"
      });
    }
  };

  // Level progress calculation
  const levelProgress = dashboard?.points 
    ? (dashboard.points.current_level_points / dashboard.points.points_to_next_level) * 100 
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Universal Page Header */}
      <PageHeader
        title="Safety Achievements"
        subtitle="Earn points, unlock badges, and climb the leaderboard!"
        icon={Trophy}
        variant="orange"
        onRefresh={loadDashboard}
        loading={loading}
      />

      {/* Stats Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Points & Level */}
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-xs">Total Points</p>
                <p className="text-2xl font-bold">{dashboard?.points?.total_points || 0}</p>
              </div>
              <Star className="w-8 h-8 text-yellow-300" />
            </div>
            <div className="mt-2">
              <div className="flex justify-between text-xs text-blue-100 mb-1">
                <span>Level {dashboard?.points?.level || 1}</span>
                <span>{dashboard?.points?.current_level_points || 0}/{dashboard?.points?.points_to_next_level || 100}</span>
              </div>
              <Progress value={levelProgress} className="h-2 bg-blue-400" />
            </div>
          </CardContent>
        </Card>

        {/* Streak */}
        <Card className="bg-gradient-to-br from-orange-500 to-red-500 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-xs">Current Streak</p>
                <p className="text-2xl font-bold">{dashboard?.points?.streak_days || 0} days</p>
              </div>
              <Flame className="w-8 h-8 text-yellow-300" />
            </div>
            <p className="text-orange-100 text-xs mt-2">Keep it going! 🔥</p>
          </CardContent>
        </Card>

        {/* Badges */}
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-xs">Badges Earned</p>
                <p className="text-2xl font-bold">
                  {dashboard?.badges?.total_earned || 0}
                  <span className="text-sm font-normal text-purple-200">
                    /{dashboard?.badges?.total_available || 20}
                  </span>
                </p>
              </div>
              <Award className="w-8 h-8 text-yellow-300" />
            </div>
          </CardContent>
        </Card>

        {/* Rank */}
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-xs">Leaderboard Rank</p>
                <p className="text-2xl font-bold">#{dashboard?.leaderboard_position?.rank || '-'}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-yellow-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4 h-auto p-1 gap-1 bg-gray-100 rounded-xl">
          <TabsTrigger 
            value="overview"
            className="flex items-center gap-2 py-2.5 rounded-lg data-[state=active]:bg-purple-600 data-[state=active]:text-white"
          >
            <Sparkles className="w-4 h-4" />
            <span className="hidden sm:inline">Overview</span>
          </TabsTrigger>
          <TabsTrigger 
            value="badges"
            className="flex items-center gap-2 py-2.5 rounded-lg data-[state=active]:bg-yellow-500 data-[state=active]:text-white"
          >
            <Medal className="w-4 h-4" />
            <span className="hidden sm:inline">Badges</span>
          </TabsTrigger>
          <TabsTrigger 
            value="challenges"
            className="flex items-center gap-2 py-2.5 rounded-lg data-[state=active]:bg-green-600 data-[state=active]:text-white"
          >
            <Target className="w-4 h-4" />
            <span className="hidden sm:inline">Challenges</span>
          </TabsTrigger>
          <TabsTrigger 
            value="leaderboard"
            className="flex items-center gap-2 py-2.5 rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white"
          >
            <Crown className="w-4 h-4" />
            <span className="hidden sm:inline">Leaderboard</span>
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Badges */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-yellow-500" />
                  Recent Badges
                </CardTitle>
              </CardHeader>
              <CardContent>
                {allBadges.earned.length > 0 ? (
                  <div className="grid grid-cols-3 gap-4">
                    {allBadges.earned.slice(0, 6).map((badge, index) => (
                      <div key={index} className="text-center">
                        <div className={`w-16 h-16 mx-auto rounded-full bg-gradient-to-br ${TIER_COLORS[badge.tier]} flex items-center justify-center text-2xl shadow-lg`}>
                          {badge.icon}
                        </div>
                        <p className="text-xs font-medium mt-2 truncate">{badge.name}</p>
                        <Badge className={`text-xs mt-1 ${TIER_BG[badge.tier]}`}>
                          {badge.tier}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Lock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No badges earned yet</p>
                    <p className="text-sm">Complete activities to earn your first badge!</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Active Challenges */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-green-500" />
                  Active Challenges
                </CardTitle>
              </CardHeader>
              <CardContent>
                {challenges.length > 0 ? (
                  <div className="space-y-3">
                    {challenges.slice(0, 3).map((challenge, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium text-sm">{challenge.name}</p>
                          <p className="text-xs text-gray-500">{challenge.description}</p>
                        </div>
                        <div className="text-right">
                          <Badge className="bg-green-100 text-green-800">
                            +{challenge.points_reward} pts
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Target className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No active challenges</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Stats Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-blue-500" />
                Your Progress
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <BookOpen className="w-8 h-8 mx-auto text-blue-600 mb-2" />
                  <p className="text-2xl font-bold text-blue-600">
                    {dashboard?.stats?.trainings_completed || 0}
                  </p>
                  <p className="text-xs text-gray-600">Trainings Completed</p>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <AlertTriangle className="w-8 h-8 mx-auto text-orange-600 mb-2" />
                  <p className="text-2xl font-bold text-orange-600">
                    {dashboard?.stats?.observations_reported || 0}
                  </p>
                  <p className="text-xs text-gray-600">Hazards Reported</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <CheckCircle className="w-8 h-8 mx-auto text-green-600 mb-2" />
                  <p className="text-2xl font-bold text-green-600">
                    {dashboard?.stats?.audits_completed || 0}
                  </p>
                  <p className="text-xs text-gray-600">Audits Completed</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <Shield className="w-8 h-8 mx-auto text-purple-600 mb-2" />
                  <p className="text-2xl font-bold text-purple-600">
                    {dashboard?.stats?.incident_free_days || 0}
                  </p>
                  <p className="text-xs text-gray-600">Incident-Free Days</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Badges Tab */}
        <TabsContent value="badges" className="space-y-6">
          {/* Earned Badges */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Unlock className="w-5 h-5 text-green-500" />
                Earned Badges ({allBadges.earned.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {allBadges.earned.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {allBadges.earned.map((badge, index) => {
                    const CategoryIcon = CATEGORY_ICONS[badge.category] || Award;
                    return (
                      <div key={index} className="text-center group cursor-pointer">
                        <div className={`w-20 h-20 mx-auto rounded-full bg-gradient-to-br ${TIER_COLORS[badge.tier]} flex items-center justify-center text-3xl shadow-lg group-hover:scale-110 transition-transform`}>
                          {badge.icon}
                        </div>
                        <p className="text-sm font-medium mt-2">{badge.name}</p>
                        <Badge className={`text-xs mt-1 ${TIER_BG[badge.tier]}`}>
                          {badge.tier} • +{badge.points} pts
                        </Badge>
                        <p className="text-xs text-gray-500 mt-1 hidden group-hover:block">
                          {badge.description}
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Lock className="w-16 h-16 mx-auto mb-3 text-gray-300" />
                  <p className="text-lg">No badges earned yet</p>
                  <p className="text-sm">Start completing activities to earn badges!</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* In Progress Badges */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-gray-400" />
                Available Badges ({allBadges.inProgress.length})
              </CardTitle>
              <CardDescription>Complete the requirements to unlock these badges</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {allBadges.inProgress.map((badge, index) => {
                  const CategoryIcon = CATEGORY_ICONS[badge.category] || Award;
                  return (
                    <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className={`w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center text-2xl grayscale opacity-50`}>
                        {badge.icon}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{badge.name}</p>
                        <p className="text-xs text-gray-500">{badge.description}</p>
                        <div className="mt-2">
                          <div className="flex justify-between text-xs text-gray-500 mb-1">
                            <span>Progress</span>
                            <span>{Math.round(badge.progress || 0)}%</span>
                          </div>
                          <Progress value={badge.progress || 0} className="h-2" />
                        </div>
                      </div>
                      <Badge className={`${TIER_BG[badge.tier]}`}>
                        {badge.tier}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Challenges Tab */}
        <TabsContent value="challenges" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Daily */}
            <Card className="border-t-4 border-t-blue-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-600">
                  <Calendar className="w-5 h-5" />
                  Daily Challenges
                </CardTitle>
              </CardHeader>
              <CardContent>
                {challenges.filter(c => c.challenge_type === 'daily').map((challenge, index) => (
                  <div key={index} className="p-4 bg-blue-50 rounded-lg mb-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{challenge.name}</p>
                        <p className="text-sm text-gray-600">{challenge.description}</p>
                      </div>
                      <Badge className="bg-blue-100 text-blue-800">
                        +{challenge.points_reward}
                      </Badge>
                    </div>
                    <Button 
                      size="sm" 
                      className="mt-3 w-full"
                      onClick={() => joinChallenge(challenge.id)}
                    >
                      Join Challenge
                    </Button>
                  </div>
                ))}
                {challenges.filter(c => c.challenge_type === 'daily').length === 0 && (
                  <p className="text-gray-500 text-center py-4">No daily challenges available</p>
                )}
              </CardContent>
            </Card>

            {/* Weekly */}
            <Card className="border-t-4 border-t-green-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-600">
                  <Clock className="w-5 h-5" />
                  Weekly Challenges
                </CardTitle>
              </CardHeader>
              <CardContent>
                {challenges.filter(c => c.challenge_type === 'weekly').map((challenge, index) => (
                  <div key={index} className="p-4 bg-green-50 rounded-lg mb-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{challenge.name}</p>
                        <p className="text-sm text-gray-600">{challenge.description}</p>
                      </div>
                      <Badge className="bg-green-100 text-green-800">
                        +{challenge.points_reward}
                      </Badge>
                    </div>
                    <Button 
                      size="sm" 
                      className="mt-3 w-full bg-green-600 hover:bg-green-700"
                      onClick={() => joinChallenge(challenge.id)}
                    >
                      Join Challenge
                    </Button>
                  </div>
                ))}
                {challenges.filter(c => c.challenge_type === 'weekly').length === 0 && (
                  <p className="text-gray-500 text-center py-4">No weekly challenges available</p>
                )}
              </CardContent>
            </Card>

            {/* Monthly */}
            <Card className="border-t-4 border-t-purple-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-purple-600">
                  <Trophy className="w-5 h-5" />
                  Monthly Challenges
                </CardTitle>
              </CardHeader>
              <CardContent>
                {challenges.filter(c => c.challenge_type === 'monthly').map((challenge, index) => (
                  <div key={index} className="p-4 bg-purple-50 rounded-lg mb-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{challenge.name}</p>
                        <p className="text-sm text-gray-600">{challenge.description}</p>
                      </div>
                      <Badge className="bg-purple-100 text-purple-800">
                        +{challenge.points_reward}
                      </Badge>
                    </div>
                    <Button 
                      size="sm" 
                      className="mt-3 w-full bg-purple-600 hover:bg-purple-700"
                      onClick={() => joinChallenge(challenge.id)}
                    >
                      Join Challenge
                    </Button>
                  </div>
                ))}
                {challenges.filter(c => c.challenge_type === 'monthly').length === 0 && (
                  <p className="text-gray-500 text-center py-4">No monthly challenges available</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Leaderboard Tab */}
        <TabsContent value="leaderboard" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-yellow-500" />
                Top Safety Champions
              </CardTitle>
              <CardDescription>See how you rank against your colleagues</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {leaderboard.length > 0 ? (
                  leaderboard.map((entry, index) => (
                    <div 
                      key={index}
                      className={`flex items-center justify-between p-4 rounded-lg transition-all ${
                        entry.is_current_user 
                          ? 'bg-blue-100 border-2 border-blue-400' 
                          : index < 3 
                            ? 'bg-gradient-to-r from-yellow-50 to-orange-50' 
                            : 'bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        {/* Rank */}
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                          index === 0 ? 'bg-yellow-400 text-yellow-900' :
                          index === 1 ? 'bg-gray-300 text-gray-700' :
                          index === 2 ? 'bg-amber-600 text-amber-100' :
                          'bg-gray-200 text-gray-600'
                        }`}>
                          {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : entry.rank}
                        </div>
                        
                        {/* User Info */}
                        <div>
                          <p className={`font-medium ${entry.is_current_user ? 'text-blue-700' : ''}`}>
                            {entry.username}
                            {entry.is_current_user && <Badge className="ml-2 bg-blue-600">You</Badge>}
                          </p>
                          <p className="text-sm text-gray-500">
                            Level {entry.level} • {entry.badges_count} badges
                          </p>
                        </div>
                      </div>
                      
                      {/* Points */}
                      <div className="text-right">
                        <p className="text-lg font-bold text-blue-600">{entry.total_points}</p>
                        <p className="text-xs text-gray-500">points</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg">Be the first on the leaderboard!</p>
                    <p className="text-sm">Start earning points to climb the ranks</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default GamificationDashboard;
